import { Check } from 'lucide-react'

export interface Step {
  id: string
  title: string
  description?: string
}

interface StepperProps {
  steps: Step[]
  currentStep: number
  onStepClick?: (stepIndex: number) => void
  allowClickNavigation?: boolean
}

export function Stepper({
  steps,
  currentStep,
  onStepClick,
  allowClickNavigation = false
}: StepperProps) {
  return (
    <nav aria-label="Progress" className="w-full overflow-x-auto">
      <ol className="flex items-center justify-between min-w-max">
        {steps.map((step, index) => {
          const isCompleted = index < currentStep
          const isCurrent = index === currentStep
          const isClickable = allowClickNavigation && index <= currentStep

          return (
            <li
              key={step.id}
              className={`relative flex items-center ${index !== steps.length - 1 ? 'flex-1' : ''}`}
            >
              {/* Step content */}
              <div className="flex flex-col items-center">
                {/* Step circle */}
                <button
                  type="button"
                  onClick={() => isClickable && onStepClick?.(index)}
                  disabled={!isClickable}
                  className={`
                    relative z-10 flex h-8 w-8 items-center justify-center rounded-full flex-shrink-0
                    ${isCompleted
                      ? 'bg-blue-600 text-white hover:bg-blue-700'
                      : isCurrent
                        ? 'border-2 border-blue-600 bg-white text-blue-600'
                        : 'border-2 border-gray-300 bg-white text-gray-500'
                    }
                    ${isClickable ? 'cursor-pointer' : 'cursor-default'}
                    transition-colors duration-200
                  `}
                >
                  {isCompleted ? (
                    <Check className="h-4 w-4" />
                  ) : (
                    <span className="text-sm font-medium">{index + 1}</span>
                  )}
                </button>

                {/* Step label - below circle */}
                <div className="mt-2 text-center max-w-[100px]">
                  <span
                    className={`text-xs font-medium block ${
                      isCurrent ? 'text-blue-600' : isCompleted ? 'text-gray-900' : 'text-gray-500'
                    }`}
                  >
                    {step.title}
                  </span>
                  {step.description && (
                    <p className="text-[10px] text-gray-400 truncate">{step.description}</p>
                  )}
                </div>
              </div>

              {/* Connector line */}
              {index !== steps.length - 1 && (
                <div
                  className={`flex-1 h-0.5 mx-2 mt-[-24px] ${
                    isCompleted ? 'bg-blue-600' : 'bg-gray-200'
                  }`}
                  aria-hidden="true"
                />
              )}
            </li>
          )
        })}
      </ol>
    </nav>
  )
}

// Vertical stepper variant for mobile
export function VerticalStepper({
  steps,
  currentStep,
  onStepClick,
  allowClickNavigation = false
}: StepperProps) {
  return (
    <nav aria-label="Progress" className="flex flex-col">
      {steps.map((step, index) => {
        const isCompleted = index < currentStep
        const isCurrent = index === currentStep
        const isClickable = allowClickNavigation && index <= currentStep

        return (
          <div key={step.id} className="relative flex items-start pb-6 last:pb-0">
            {/* Connector line */}
            {index !== steps.length - 1 && (
              <div
                className={`absolute left-4 top-8 -bottom-2 w-0.5 ${
                  isCompleted ? 'bg-blue-600' : 'bg-gray-200'
                }`}
                aria-hidden="true"
              />
            )}

            {/* Step circle */}
            <button
              type="button"
              onClick={() => isClickable && onStepClick?.(index)}
              disabled={!isClickable}
              className={`
                relative z-10 flex h-8 w-8 items-center justify-center rounded-full flex-shrink-0
                ${isCompleted
                  ? 'bg-blue-600 text-white'
                  : isCurrent
                    ? 'border-2 border-blue-600 bg-white text-blue-600'
                    : 'border-2 border-gray-300 bg-white text-gray-500'
                }
                ${isClickable ? 'cursor-pointer hover:bg-blue-700' : 'cursor-default'}
                transition-colors duration-200
              `}
            >
              {isCompleted ? (
                <Check className="h-4 w-4" />
              ) : (
                <span className="text-sm font-medium">{index + 1}</span>
              )}
            </button>

            {/* Step content */}
            <div className="ml-4">
              <span
                className={`text-sm font-medium ${
                  isCurrent ? 'text-blue-600' : isCompleted ? 'text-gray-900' : 'text-gray-500'
                }`}
              >
                {step.title}
              </span>
              {step.description && (
                <p className="mt-0.5 text-xs text-gray-500">{step.description}</p>
              )}
            </div>
          </div>
        )
      })}
    </nav>
  )
}

export default Stepper
